import analyticstracker from "@jmc/analyticstracker";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { FilterSelectOptionType } from "@jmc/solid-design-system/src/components/atoms/Select/Select.Helpers";
import { FilterCategory } from "@jmc/solid-design-system/src/components/organisms/FilterControlPanel/FilterControlPanel";
import React, { useEffect, useRef } from "react";

interface PropType {
    children: JSX.Element;
    ignore?: string[];
}

const FilterImpression: React.FC<PropType> = ({ children, ignore = [] }: PropType): JSX.Element => {
    const ref = useRef(null);

    //change to filter via tag
    useEffect(() => {
        const handleFilterChangeByTag = (event: CustomEvent<{ filterResult: FilterCategory[] }>) => {
            ref.current = null;
            const { filterResult } = event.detail;
            let stringified: string;

            //remove all categories with no filters selected
            const selectedCategories = filterResult.filter((c) => {
                const previouslySelected = c.options.filter((o) => o.isSelected);
                return previouslySelected.length > 0 ? true : false;
            });

            //change the format for analytics part 1
            let simplified = selectedCategories.map((c) => {
                const reducedOptions = c.options.filter((o) => o.isSelected);
                return { name: c.name, values: reducedOptions.map((ro) => ro.label) };
            });

            //remove any and all filter on the ignore list
            if (ignore.length > 0) {
                simplified = simplified.map((s) => {
                    if (ignore.map((i) => i.toLowerCase()).includes(s.name.toLowerCase()) && s.values.length > 0) {
                        return { ...s, values: ["untracked"] };
                    } else {
                        return s;
                    }
                });
            }

            //change the format for analytics part 2
            if (simplified.length === 0) {
                stringified = "removed";
            } else {
                const simplifiedString = simplified
                    .filter((s) => s.values.length > 0)
                    .map((s) => `${s.name}:${s.values.toString()}`);
                stringified = simplifiedString.join("|");
            }

            ref.current = {
                name: new URL((event.target as Document).URL).pathname.split("/").slice(-1).toString(),
                filter: stringified,
                component: "tag",
            };
        };

        document.addEventListener(EventTypes.FILTER_TAG, handleFilterChangeByTag);
        return (): void => {
            document.removeEventListener(EventTypes.FILTER_TAG, handleFilterChangeByTag);
        };
    });

    //change to filter via algolia tag
    useEffect(() => {
        const handleFilterChangeByALGOLIATag = (
            event: CustomEvent<{ AlgoliaFacet: { category: string; value: string } }>,
        ) => {
            const { AlgoliaFacet } = event.detail;

            if (ignore.includes(AlgoliaFacet.category)) {
                AlgoliaFacet.value = "untracked";
            }

            if (!ref.current) {
                ref.current = {
                    name: new URL((event.target as Document).URL).pathname.split("/").slice(-1).toString(),
                    filter: `${AlgoliaFacet.category}:${AlgoliaFacet.value}`,
                    component: "tag",
                };
            } else {
                const filters: string[] = ref.current.filter.split("|");

                //check if category already exist
                const existingCategory = filters.findIndex((c) => {
                    return c.includes(AlgoliaFacet.category);
                });

                //if not add it
                if (existingCategory === -1) {
                    filters.push(`${AlgoliaFacet.category}:${AlgoliaFacet.value}`);
                }
                //if yes check if value already exist and add it if it doesn't
                if (existingCategory !== -1 && !filters[existingCategory].includes(AlgoliaFacet.value)) {
                    filters[existingCategory] = `${filters[existingCategory]},${AlgoliaFacet.value}`;
                }

                const stringified = filters.join("|");
                ref.current = {
                    name: new URL((event.target as Document).URL).pathname.split("/").slice(-1).toString(),
                    filter: stringified,
                    component: "tag",
                };
            }
        };

        document.addEventListener(EventTypes.FILTER_TAG_ALGOLIA, handleFilterChangeByALGOLIATag);
        return (): void => {
            document.removeEventListener(EventTypes.FILTER_TAG_ALGOLIA, handleFilterChangeByALGOLIATag);
        };
    });

    //change to filter via dropdown
    useEffect(() => {
        const handleFilterChange = (
            event: CustomEvent<{
                data: FilterSelectOptionType;
                filterCategory: FilterCategory;
                filters: FilterCategory[];
            }>,
        ) => {
            ref.current = null;
            const { data, filterCategory, filters } = event.detail;
            let stringified: string;
            //clearing an entire filter category
            if (Array.isArray(data)) {
                const selectedCategories = filters.filter((f) => f.id !== filterCategory.id);

                let simplified = selectedCategories.map((c) => {
                    const reducedOptions = c.options.filter((o) => o.isSelected);
                    return { name: c.name, values: reducedOptions.map((ro) => ro.label) };
                });

                if (ignore.length > 0) {
                    simplified = simplified.map((s) => {
                        if (ignore.map((i) => i.toLowerCase()).includes(s.name.toLowerCase()) && s.values.length > 0) {
                            return { ...s, values: ["untracked"] };
                        } else {
                            return s;
                        }
                    });
                }

                const simplifiedString = simplified
                    .filter((s) => s.values.length > 0)
                    .map((s) => `${s.name}:${s.values.toString()}`);
                simplifiedString.push(`${filterCategory.name}:removed`);
                stringified = simplifiedString.join("|");
            } else {
                //step 1 find all the existing categories already containing a value that is selected
                const selectedCategories = filters.filter((c) => {
                    const previouslySelected = c.options.filter((o) => o.isSelected);
                    return previouslySelected.length > 0 ? true : false;
                });
                //step 2 reduce values to category name + list of options name
                let simplified = selectedCategories.map((c) => {
                    const reducedOptions = c.options.filter((o) => o.isSelected);
                    return { name: c.name, values: reducedOptions.map((ro) => ro.label) };
                });
                //step 3 add in or remove currently selected value
                const found = simplified.findIndex((s) => s.name === filterCategory.name);
                if (found >= 0) {
                    data.isSelected
                        ? (simplified[found].values = simplified[found].values.filter((v) => v !== data.label))
                        : simplified[found].values.push(data.label);
                } else {
                    simplified.push({ name: filterCategory.name, values: [data.label] });
                }
                if (ignore.length > 0) {
                    simplified = simplified.map((s) => {
                        if (ignore.map((i) => i.toLowerCase()).includes(s.name.toLowerCase()) && s.values.length > 0) {
                            return { ...s, values: ["untracked"] };
                        } else {
                            return s;
                        }
                    });
                }
                const simplifiedString = simplified
                    .filter((s) => s.values.length > 0)
                    .map((s) => `${s.name}:${s.values.toString()}`);
                stringified = simplifiedString.join("|");
            }

            ref.current = {
                name: new URL((event.target as Document).URL).pathname.split("/").slice(-1).toString(),
                filter: stringified,
                component: "dropdown",
            };
        };
        document.addEventListener(EventTypes.FILTER_IMPRESSION, handleFilterChange);
        return (): void => {
            document.removeEventListener(EventTypes.FILTER_IMPRESSION, handleFilterChange);
        };
    });

    //count
    useEffect(() => {
        const handleCount = (event: CustomEvent<{ count: number }>) => {
            if (ref.current) {
                analyticstracker().trackEvent({
                    event: EventTypes.FILTER_IMPRESSION,
                    info: { ...ref.current, resultcount: event.detail.count },
                });
            }
        };
        document.addEventListener(EventTypes.FILTER_COUNT, handleCount);
        return (): void => {
            document.removeEventListener(EventTypes.FILTER_COUNT, handleCount);
        };
    });

    //reset
    useEffect(() => {
        const handleReset = (event: CustomEvent) => {
            ref.current = null;
            analyticstracker().trackEvent({
                event: EventTypes.FILTER_IMPRESSION,
                info: {
                    name: new URL((event.target as Document).URL).pathname.split("/").slice(-1).toString(),
                    filter: "removed",
                    component: "full reset",
                    resultcount: "unset",
                },
            });
        };
        document.addEventListener(EventTypes.FILTER_RESET, handleReset);
        return (): void => {
            document.removeEventListener(EventTypes.FILTER_RESET, handleReset);
        };
    });

    return <>{children}</>;
};

export default FilterImpression;
